import { ConfigClient, ConfigCollection } from '@atlaskit/web-config-client';
import React, { createContext, useEffect, useState, useContext } from 'react';
import { getConfig } from '../../config/envConfig';
import { logErrorMessage } from '../../utils/sentry';

const FETCH_TIMEOUT_MS = 10000;

const { statsigApiKey, statsigDynConfigUrl } = getConfig();

interface DynamicConfigContextState {
  isInitialised: boolean;
  config: ConfigCollection | undefined;
}

const defaultState: DynamicConfigContextState = {
  isInitialised: false,
  config: undefined,
};

export const DynamicConfigContext = createContext(defaultState);

export const DynamicConfigProvider = ({
  children,
  userId,
  onReady,
}: {
  children: React.ReactNode;
  userId?: string;
  onReady?: (error?) => void;
}) => {
  const [isInitialised, setIsInitialised] = useState<boolean>(false);
  const [config, setConfig] = useState<ConfigCollection | undefined>(undefined);

  useEffect(() => {
    if (!userId) {
      // React v18 require us to return clean-up function
      return () => {};
    }
    ConfigClient.fetch({
      ffsApiKey: statsigApiKey,
      ffsBaseUrl: statsigDynConfigUrl,
      context: {
        namespace: 'manage-profile-ui_web',
        identifiers: {
          atlassianAccountId: userId,
        },
        metadata: {},
      },
      fetch: (input: RequestInfo | URL, init?: RequestInit) =>
        fetch(input, {
          ...init,
          signal: AbortSignal?.timeout?.(FETCH_TIMEOUT_MS),
        }),
    })
      .then(configCollection => {
        setConfig(configCollection);
        setIsInitialised(true);
        if (onReady) {
          onReady();
        }
      })
      .catch(error => {
        void logErrorMessage('Statsig dynamicConfig failed to be initialised', {
          error,
        });
        setIsInitialised(false);
        setConfig(ConfigCollection.empty());
        if (onReady) {
          onReady(error);
        }
      });
  }, [userId, onReady]);

  return (
    <DynamicConfigContext.Provider
      value={{
        isInitialised,
        config,
      }}
    >
      {children}
    </DynamicConfigContext.Provider>
  );
};

export const useDynamicConfig = () => {
  const context = useContext(DynamicConfigContext);

  if (!context) {
    throw new Error(
      'useDynamicConfigClient used outside of DynamicConfigProvider.',
    );
  }

  return context;
};
